// Hoja lateral (rediseño, docs/PLAN-REDISENO-PORTAL.md decision 4): el
// detalle que no cabe en la fila (hoja de ruta, recorrido, documentos, alta)
// se abre a la derecha sin romper la lista; en 390 px ocupa la pantalla.
//
// Es un dialogo modal propio, sin dependencia nueva: foco al cerrar al
// abrir, Tab que no sale de la hoja, Escape y clic afuera que cierran, foco
// devuelto a quien la abrio. Se renderiza en el lugar (sin portal) para que
// el contenido quede dentro de la fila que lo abrio en el arbol accesible.
import { useEffect, useId, useRef } from "react";
import { cn } from "./cn.js";

interface Props {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  /** Linea bajo el titulo: de que viaje o de que vehiculo es la hoja. */
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  /** Acciones fijas abajo, ya armadas. */
  footer?: React.ReactNode;
  className?: string;
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Sheet({ open, onClose, title, subtitle, children, footer, className }: Props): React.JSX.Element | null {
  const titleId = useId();
  const panel = useRef<HTMLElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement;
    closeButton.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== "Tab" || panel.current === null) return;
      const items = Array.from(panel.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      const first = items[0];
      const last = items[items.length - 1];
      if (first === undefined || last === undefined) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
      if (opener instanceof HTMLElement) opener.focus();
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="hoja-lateral fixed inset-0 z-[70] flex justify-end">
      <div aria-hidden="true" className="absolute inset-0 bg-pagina/70" onClick={onClose} />
      <section
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cn(
          "hoja-lateral-panel entrada-lateral relative flex h-full w-full flex-col bg-superficie text-texto shadow-panel md:w-[min(600px,92vw)] md:border-l md:border-linea",
          className,
        )}
      >
        <header className="flex items-start justify-between gap-3 border-b border-divisor px-4 py-3">
          <div className="min-w-0">
            <h2 id={titleId} className="m-0 text-base font-semibold leading-tight text-texto">
              {title}
            </h2>
            {subtitle !== undefined && <p className="m-0 mt-0.5 truncate text-xs text-texto-suave">{subtitle}</p>}
          </div>
          <button
            ref={closeButton}
            type="button"
            aria-label="Cerrar"
            onClick={onClose}
            className="grid size-8 shrink-0 place-items-center rounded-md text-sm text-texto-suave transition-colors duration-150 ease-estandar hover:bg-velo hover:text-texto"
          >
            ✕
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto p-4">{children}</div>
        {footer !== undefined && <footer className="hoja-sin-imprimir flex flex-wrap gap-2 border-t border-divisor px-4 py-3">{footer}</footer>}
      </section>
    </div>
  );
}
